import * as z from 'zod';

export const QueryPaymentAcceptanceConfigurationsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('WQL expression.'),
});
export const QueryPaymentAcceptanceConfigurationsResponse = z.object({
  paymentAcceptanceConfigurations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Id')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        appId: z
          .string()
          .describe('App id')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        rules: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Rule Id')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              paymentMethodTypeId: z
                .string()
                .describe('Payment method type id')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              accountConnectionId: z
                .string()
                .describe('Account connection id')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              merchantEnabled: z
                .boolean()
                .describe('Merchant enabled')
                .optional(),
            })
          )
          .max(1000)
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe('Revision')
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date when PaymentAcceptanceConfiguration was created')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date when PaymentAcceptanceConfiguration was updated')
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data Extensions')
          .optional(),
        tags: z
          .object({
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
